import * as React from "react";
import { Alert, Divider, Switch } from "antd";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpSelect } from "../../../gvc/testNewForm/CpSelect";
import { CpDescription } from "../../../gvc/testNewForm/CpDescription";
import { toSelectOptions } from "../../../../services/defaults";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { CpKindSelect } from "../../../gvc/testNewForm/temp/CpKindSelect";
import { CpLabelTooltip } from "../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../gvc/testNewForm/CpTooltip";
import { isInternalDomain } from "../../../../services/utils";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";

const allCipherSuiteOptions = [
  "ECDHE-ECDSA-AES256-GCM-SHA384",
  "ECDHE-ECDSA-CHACHA20-POLY1305",
  "ECDHE-ECDSA-AES128-GCM-SHA256",
  "ECDHE-RSA-AES256-GCM-SHA384",
  "ECDHE-RSA-CHACHA20-POLY1305",
  "ECDHE-RSA-AES128-GCM-SHA256",
  "AES256-GCM-SHA384",
  "AES128-GCM-SHA256",
];

interface Props {
  index: number;
}
export const PortTls: React.FC<Props> = ({ index }) => {
  const { id } = useCpFormContext();
  const port = useCpFormValue(`advanced.spec.ports.${index}`);

  const name = useCpFormValue("advanced.name");
  const isInternal = React.useMemo(() => isInternalDomain(name), [name]);

  const dnsMode = useCpFormValue("advanced.spec.dnsMode");
  const routingMode = useCpFormValue("advanced.spec.routingMode");
  const certChallengeType = useCpFormValue("advanced.spec.certChallengeType");
  const isTlsEnabled = useCpFormValue(`advanced.spec.ports.${index}.tls.isEnabled`);

  const isClientEnabled = useCpFormValue(`advanced.spec.ports.${index}.tls.clientCertificate.isEnabled`);

  const acceptAll = useCpFormValue("advanced.spec.acceptAll");

  const isServerEnabled = useCpFormValue(`advanced.spec.ports.${index}.tls.serverCertificate.isEnabled`);
  const serverSecret = useCpFormValue(`advanced.spec.ports.${index}.tls.serverCertificate.secret.value`);

  const isGvcSubdomain = routingMode === "gvcSubdomain";
  const isAcceptAll = acceptAll === "hosts" || acceptAll === "subdomains";
  const requiresServerCert = isInternal || (dnsMode === "cname" && certChallengeType !== "dns01" && (isGvcSubdomain || isAcceptAll));

  const mustEnableTLS = (!port.number || port.number === "443") && port.protocol !== "tcp";

  React.useEffect(() => {
    if (isInternal && (isTlsEnabled || mustEnableTLS)) {
      formSetValue(id, `advanced.spec.ports.${index}.tls.serverCertificate.isEnabled`, true);
    }
  }, [isInternal, isTlsEnabled, mustEnableTLS]);

  return (
    <div key={`port-tls-${index}`} className="mb-8">
      <Divider orientation="left" orientationMargin={0}>
        TLS
      </Divider>
      {mustEnableTLS ? (
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name="skip">Enable TLS</CpLabel>
            <Switch size="small" checked={true} disabled />
          </div>
          <CpDescription>TLS is always enabled when number is 443 and protocol is either http or http2</CpDescription>
        </CpFormItem>
      ) : (
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name={`advanced.spec.ports.${index}.tls.isEnabled`}>Enable TLS</CpLabel>
            <CpSwitch name={`advanced.spec.ports.${index}.tls.isEnabled`} />
          </div>
        </CpFormItem>
      )}
      {isTlsEnabled || mustEnableTLS ? (
        <>
          <CpFormItem>
            <CpLabel name={`advanced.spec.ports.${index}.tls.minProtocolVersion`}>Min Protocol Version</CpLabel>
            <CpSelect
              name={`advanced.spec.ports.${index}.tls.minProtocolVersion`}
              options={[
                { label: "TLSV1_3", value: "TLSV1_3" },
                { label: "TLSV1_2", value: "TLSV1_2" },
                { label: "TLSV1_1", value: "TLSV1_1" },
                { label: "TLSV1_0", value: "TLSV1_0" },
              ]}
              style={{ maxWidth: 600 }}
            />
          </CpFormItem>
          <CpFormItem>
            <div className="flex items-center gap-2 mb-2">
              <CpLabel name={`advanced.spec.ports.${index}.tls.clientCertificate.isEnabled`}>
                Forward Client Certificate
              </CpLabel>
              {/* required */}
              <CpSwitch name={`advanced.spec.ports.${index}.tls.clientCertificate.isEnabled`} />
            </div>
            {isClientEnabled ? (
              <div className="flex flex-col">
                <CpLabelTooltip>
                  <CpLabel name={`advanced.spec.ports.${index}.tls.clientCertificate.secret`}>
                    Client Certificate Authority PEM
                  </CpLabel>
                  <CpTooltip tooltipProps={{ title: `A secret of type TLS` }} />
                </CpLabelTooltip>
                <CpKindSelect
                  name={`advanced.spec.ports.${index}.tls.clientCertificate.secret`}
                  optionValue="name"
                  kind={"secret"}
                  queries={[{ property: "type", value: "tls" }]}
                  style={{ maxWidth: 600 }}
                />
                <CpErrorWarning name={`advanced.spec.ports.${index}.tls.clientCertificate.secret`} />
              </div>
            ) : null}
          </CpFormItem>
          <CpFormItem>
            <div className="flex items-center gap-2 mb-2">
              <CpLabel name={`advanced.spec.ports.${index}.tls.serverCertificate.isEnabled`}>
                Use Custom Server Certificate
              </CpLabel>
              <CpSwitch name={`advanced.spec.ports.${index}.tls.serverCertificate.isEnabled`} />
            </div>
            {isServerEnabled ? (
              <div className="flex flex-col">
                <CpLabelTooltip>
                  <CpLabel name={`advanced.spec.ports.${index}.tls.serverCertificate.secret`}>
                    Server Certificate Authority PEM
                  </CpLabel>
                  <CpTooltip tooltipProps={{ title: `A secret of type TLS` }} />
                </CpLabelTooltip>
                <CpKindSelect
                  name={`advanced.spec.ports.${index}.tls.serverCertificate.secret`}
                  optionValue="name"
                  kind={"secret"}
                  queries={[{ property: "type", value: "tls" }]}
                  style={{ maxWidth: 600 }}
                />
                <CpErrorWarning name={`advanced.spec.ports.${index}.tls.serverCertificate.secret`} />
              </div>
            ) : null}
            {requiresServerCert ? (
              <Alert
                showIcon
                className="my-1 w-fit"
                type={!serverSecret ? "error" : "success"}
                message="Requirement"
                description={
                  isInternal
                    ? "Internal domains require a server certificate (TLS secret) on all ports with TLS enabled."
                    : isGvcSubdomain
                      ? `Using CNAME and GVC Subdomain requires Server Certificate Auth PEM to be set on all ports with tls enabled.`
                      : `Using CNAME and Accept All ${acceptAll === "hosts" ? "Hosts" : "Subdomains"} requires Server Certificate Auth PEM to be set on all ports with tls enabled when Cert Challenge Type is not DNS01.`
                }
              />
            ) : null}
          </CpFormItem>
          <CpFormItem>
            <CpLabel name={`advanced.spec.ports.${index}.tls.cipherSuites`}>Cipher Suites</CpLabel>
            <CpSelect
              name={`advanced.spec.ports.${index}.tls.cipherSuites`}
              options={toSelectOptions(allCipherSuiteOptions)}
              mode={`multiple`}
              style={{ width: 400 }}
            />
            <CpErrorWarning name={`advanced.spec.ports.${index}.tls.cipherSuites`} />
          </CpFormItem>
        </>
      ) : null}
    </div>
  );
};

{
  /*
{routingMode === "gvcSubdomain" && dnsMode === "cname" ? (
  <span className="color-danger">*</span>
) : null} 
*/
}
